import isNil from "ramda/es/isNil";
import isNilOrEmptyString from "ramda-extension/es/isNilOrEmptyString";
import isNumeric from "ramda-extension/es/isNumeric";
import isStringUtil from "ramda-extension/es/isString";
const emailRegExp = /^(([^<>()\[\].,;:\s@"]+(\.[^<>()\[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
const emailDMORegExp = /^(?=.{1,64}@)(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*\")@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
/**
 * Validate string
 *
 * @documented
 * @category Text
 * @import import { isString } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {Number} [input.minLength]
 * @param {Number} [input.maxLength]
 * @param {Number} [input.length]
 * @returns {String} errMessage
 *
 */ function isString({ minLength = null, maxLength = null, length = null }) {
    return ({ fieldValue })=>{
        let value = fieldValue;
        let len = 0;
        let result = false;
        let stringable = true;
        if (!isStringUtil(fieldValue)) {
            if (isNumeric(fieldValue)) {
                value = String(fieldValue);
            } else {
                stringable = false;
            }
        }
        if (stringable) {
            len = value.length;
            if (!isNil(length) && len !== length) {
                result = 'stringShouldHaveLength';
            } else if (!isNil(minLength) && len < minLength) {
                result = 'stringShouldHaveMinLength';
            } else if (!isNil(maxLength) && len > maxLength) {
                result = 'stringShouldHaveMaxLength';
            }
        } else {
            result = 'shouldBeString';
        }
        return result;
    };
}
/**
 * allow only chars in specified regexp
 *
 * @documented
 * @category Text
 * @import import { onlyAllowedChars } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {RegExp} input.regex
 * @param {String} [input.messageKey=onlyAllowedChars]
 * @returns {String} errMessage
 *
 */ function onlyAllowedChars({ regex, messageKey = 'onlyAllowedChars' }) {
    return ({ fieldValue })=>regex.test(fieldValue) ? messageKey : false;
}
/**
 * allow only chars outside of specified regexp
 *
 * @documented
 * @category Text
 * @import import { onlyAllowedCharsDisjoint } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {RegExp} input.regex
 * @param {String} [input.messageKey=onlyAllowedChars]
 * @returns {String} errMessage
 *
 */ function onlyAllowedCharsDisjoint({ regex, messageKey = 'onlyAllowedChars' }) {
    return ({ fieldValue })=>!regex.test(fieldValue) ? messageKey : false;
}
/**
 * Validate email
 *
 * @documented
 * @category Text
 * @import import { isEmail } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {Boolean} [input.allowDiacritics=true]
 * @returns {String} errMessage
 *
 */ function isEmail({ allowDiacritics = true } = {}) {
    return ({ fieldValue })=>{
        if (isNilOrEmptyString(fieldValue)) {
            return false;
        } else if (!isStringUtil(fieldValue) || !fieldValue.includes('@')) {
            return 'emailShouldContainAt';
        } else if (!/\.[^.@]{2,}$/.test(fieldValue)) {
            return 'emailShouldContainDomain';
        } else if (/\s/.test(fieldValue)) {
            return 'shouldHaveNoWhitespaces';
        } else if (!emailRegExp.test(fieldValue)) {
            return 'emailShouldHaveValidFormat';
        } else if (!allowDiacritics && /[À-ž]/.test(fieldValue)) {
            return 'emailContainsDiacritics';
        } else if (!/^[A-Za-z0-9.-]+$/.test(fieldValue.split('@')[1])) {
            return 'emailDomainContainsValidChars';
        } else {
            return false;
        }
    };
}
/**
 * Validate email with stricter regexp than isEmail and maximum length 256
 *
 * @documented
 * @category Text
 * @import import { isEmailDMO } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @returns {String} errMessage
 *
 */ function isEmailDMO() {
    return ({ fieldValue })=>{
        if (isNilOrEmptyString(fieldValue)) {
            return false;
        } else if (!isStringUtil(fieldValue) || !fieldValue.includes('@')) {
            return 'emailShouldContainAt';
        } else if (!/\.[^.@]{2,}$/.test(fieldValue)) {
            return 'emailShouldContainDomain';
        } else if (/\s/.test(fieldValue)) {
            return 'shouldHaveNoWhitespaces';
        } else if (fieldValue.length > 256) {
            return 'emailShouldBeShorter';
        } else if (!emailDMORegExp.test(fieldValue)) {
            return 'emailShouldHaveValidFormat';
        } else if (!/^[A-Za-z0-9.-]+$/.test(fieldValue.split('@')[1])) {
            return 'emailDomainContainsValidChars';
        } else {
            return false;
        }
    };
}
/**
 * Validate company name
 *
 * @documented
 * @category Text
 * @import import { isCompanyName } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ function isCompanyName() {
    return onlyAllowedChars({
        regex: new RegExp('[#&@;|;;<>]{1,50}'),
        messageKey: 'companyNameIncorrect'
    });
}
/**
 * Validate phone number
 *
 * @documented
 * @category Text
 * @import import { isPhoneNumber } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ function isPhoneNumber() {
    return onlyAllowedCharsDisjoint({
        regex: new RegExp('^(([+]|00)\\s?[1-9][0-9]{3,14}|[0-9]{9})$'),
        messageKey: 'phoneIncorrect'
    });
}
/**
 * hasNoWhitespace
 *
 * @documented
 * @category Text
 * @import import { hasNoWhitespace } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ function hasNoWhitespace() {
    return onlyAllowedChars({
        regex: /\s/,
        messageKey: 'shouldHaveNoWhitespaces'
    });
}
/**
 * hasNoPunctation
 *
 * @documented
 * @category Text
 * @import import { hasNoPunctation } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {Boolean} [input.strict=true]
 * @returns {String} errMessage
 *
 */ function hasNoPunctation({ strict = true } = {}) {
    return (input)=>{
        const result = onlyAllowedChars({
            regex: /[.,\/#!$%\^&@+\*;:{}=\-_`~()\<\>\|\[\]]/,
            messageKey: 'shouldHaveNoPunctation'
        })(input);
        if (!result && strict) {
            return onlyAllowedChars({
                regex: /[\u00DF\u011A\u016E]/,
                messageKey: 'shouldHaveNoPunctationStrict'
            })(input);
        }
        return result;
    };
}
/**
 * isAlphanumerical
 *
 * @documented
 * @category Text
 * @import import { isAlphanumerical } from '@gef-ui/validations/validationFunctions/text'
 * @param {Object} input
 * @param {Boolean} [input.onlyUpperCase=false]
 * @param {Boolean} [input.onlyLowerCase=false]
 * @returns {String} errMessage
 *
 */ function isAlphanumerical({ onlyUpperCase, onlyLowerCase } = {}) {
    if (onlyLowerCase) {
        return onlyAllowedChars({
            regex: /[^0-9a-z]/,
            messageKey: 'isAlphanumericalLower'
        });
    } else if (onlyUpperCase) {
        return onlyAllowedChars({
            regex: /[^0-9A-Z]/,
            messageKey: 'isAlphanumericalUpper'
        });
    }
    return onlyAllowedChars({
        regex: /[^0-9a-zA-Z]/,
        messageKey: 'isAlphanumerical'
    });
}
/**
 * validate vin according to https://en.wikipedia.org/wiki/Vehicle_identification_number
 *
 * @documented
 * @category Text
 * @import import { isVin } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ const isVin = ()=>onlyAllowedCharsDisjoint({
        regex: /^[a-hj-npr-z0-9]{17}$/i,
        messageKey: 'incorrectVinFormat'
    });
/**
 * validate vat
 *
 * @documented
 * @category Text
 * @import import { isVat } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ const isVat = ()=>({ fieldValue })=>{
        const regex = /\b[A-Z]{2}[A-Z0-9]{7,12}\b/;
        return regex.test(fieldValue) ? false : 'incorrectVatFormat';
    };
/**
 * validate street and orientation number when in one field. like street/orientation
 *
 * @documented
 * @category Text
 * @import import { isStreetOrientationNumber } from '@gef-ui/validations/validationFunctions/text'
 * @returns {String} errMessage
 *
 */ const isStreetOrientationNumber = ()=>({ fieldValue, ...rest })=>{
        const regex = /^[0-9a-zA-Zá-žÁ-Ž.\-\s\/]+$/;
        const firstChar = fieldValue.charAt(0);
        const lastChar = fieldValue.slice(-1);
        const dotAtFirstChar = firstChar === '.';
        const slashAtFirstOrLastChar = firstChar === '/' || lastChar === '/';
        const slashOccurrencesGreaterThanOne = (fieldValue.match(/\//g) || []).length > 1;
        if (dotAtFirstChar || slashAtFirstOrLastChar || slashOccurrencesGreaterThanOne) {
            return 'shouldContainStreetNumberSymbols';
        }
        return onlyAllowedCharsDisjoint({
            regex,
            messageKey: 'shouldContainStreetNumberSymbols'
        })({
            fieldValue,
            ...rest
        });
    };
export default {
    isString,
    isEmail,
    isEmailDMO,
    isCompanyName,
    isPhoneNumber,
    hasNoWhitespace,
    hasNoPunctation,
    isAlphanumerical,
    onlyAllowedChars,
    onlyAllowedCharsDisjoint,
    isVin,
    isVat,
    isStreetOrientationNumber
};
